// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/requisicaoService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type RequisicaoListaResumo } from './listaFiltros';
import type {
  RequisicaoResumo,
  RequisicaoDetalhe,
  CriarRequisicaoRequest,
  AtualizarRequisicaoRequest,
} from '../types';
import { paramsConsulta, type FiltroConsultaOperacional } from '../consultas';

const BASE = '/api/requisicoes';

/** KPIs de GET /api/requisicoes/resumo — consulta inteira, sem o status. */
export interface RequisicaoKpi {
  total: number;
  pendentes: number;
  aprovadas: number;
  realizadas: number;
}

/** Linha da listagem com o nome do setor (local de estoque) e do solicitante (usuário). */
export type RequisicaoListagem = RequisicaoResumo & { setorNome?: string | null; solicitanteNome?: string | null };

/** Filtros da consulta de requisições: busca, status, dtInicio/dtFim (movimento), setorId, solicitanteId. */
export type FiltroRequisicoes = FiltroConsultaOperacional & { setorId?: number; solicitanteId?: number };

export const requisicaoService = {
  /**
   * Lista paginada + `resumo` (KPIs v118). Aceita os filtros da v118 (`decrescente`) e os da
   * paridade (`direcao` e filtros extras) — o servidor entende os dois.
   */
  async getAll(pagina = 1, tamanhoPagina = 20, filtros?: FiltroRequisicoes | ListaFiltros): Promise<PaginadoComResumo<RequisicaoListagem, RequisicaoListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<RequisicaoListagem, RequisicaoListaResumo>>(BASE, {
      params: { ...paramsDaLista(pagina, tamanhoPagina), ...paramsConsulta(filtros as FiltroRequisicoes) },
    });
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros?: FiltroRequisicoes): Promise<RequisicaoKpi> {
    const { status: _s, ordenarPor: _o, direcao: _d, ...resto } = filtros ?? {};
    const { data } = await apiClient.get<RequisicaoKpi>(`${BASE}/resumo`, { params: paramsConsulta(resto) });
    return data;
  },

  async getPendentes(): Promise<RequisicaoResumo[]> {
    const { data } = await apiClient.get<RequisicaoResumo[]>(`${BASE}/pendentes`);
    return data;
  },

  async getById(id: number): Promise<RequisicaoDetalhe> {
    const { data } = await apiClient.get<RequisicaoDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarRequisicaoRequest): Promise<RequisicaoDetalhe> {
    const { data } = await apiClient.post<RequisicaoDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarRequisicaoRequest): Promise<RequisicaoResumo> {
    const { data } = await apiClient.put<RequisicaoResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
